import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * In-page replacements for window.confirm and window.prompt. Many phone browsers (and home-screen web apps) silently ignore those
 * pop-ups, so a button that relied on them appeared to do nothing.
 *
 *   const dialog = useDialog();
 *   if (!(await dialog.confirm({ title: "Delete this?", body: "...", confirmLabel: "Delete", danger: true }))) return;
 *   const reason = await dialog.prompt({ title: "Report", label: "What is wrong?", maxLength: 300 });   // string, or null if cancelled
 *   ...and render {dialog.node} once in the component's output.
 */
export function useDialog() {
  const [state, setState] = useState(null);       // { opts, resolve, value }
  const stateRef = useRef(null);
  stateRef.current = state;

  const open = useCallback((opts) => new Promise((resolve) => setState({ opts, resolve, value: "" })), []);
  const close = useCallback((result) => {
    const s = stateRef.current;
    if (!s) return;
    setState(null);
    s.resolve(result);
  }, []);
  const confirm = useCallback((opts) => open({ ...opts, kind: "confirm" }), [open]);
  const prompt = useCallback((opts) => open({ ...opts, kind: "prompt" }), [open]);

  // Leaving the page with a question open must not leave a promise hanging.
  useEffect(() => () => { if (stateRef.current) stateRef.current.resolve(stateRef.current.opts.kind === "prompt" ? null : false); }, []);

  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(state.opts.kind === "prompt" ? null : false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  let node = null;
  if (state) {
    const { opts, value } = state;
    const isPrompt = opts.kind === "prompt";
    const cancel = () => close(isPrompt ? null : false);
    const ok = () => close(isPrompt ? value.trim() : true);
    node = (
      <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label={opts.title}
        data-testid="dialog" onClick={(e) => { if (e.target === e.currentTarget) cancel(); }}>
        <form className="w-full max-w-md rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl" onSubmit={(e) => { e.preventDefault(); ok(); }}>
          <h2 className="font-serif text-xl font-semibold" data-testid="dialog-title">{opts.title}</h2>
          {opts.body && <p className="mt-2 text-sm text-muted-foreground" data-testid="dialog-body">{opts.body}</p>}
          {isPrompt && (
            <div className="mt-3">
              {opts.label && <label htmlFor="dialog-input" className="mb-1 block text-sm font-medium">{opts.label}</label>}
              <textarea id="dialog-input" data-testid="dialog-input" autoFocus rows={3} maxLength={opts.maxLength || 300} value={value} placeholder={opts.placeholder || ""}
                onChange={(e) => setState((s) => (s ? { ...s, value: e.target.value } : s))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base" />
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={cancel} data-testid="dialog-cancel" className="rounded-full px-5 py-2.5 text-sm hover:bg-secondary">{opts.cancelLabel || "Cancel"}</button>
            <button type="submit" data-testid="dialog-ok" className={`rounded-full px-6 py-2.5 text-sm font-semibold ${opts.danger ? "bg-red-600 text-white" : "bg-[#C89B3C] text-black"}`}>{opts.confirmLabel || "OK"}</button>
          </div>
        </form>
      </div>
    );
  }
  return { confirm, prompt, node: node && typeof document !== "undefined" ? createPortal(node, document.body) : node };
}